import * as amplitude from "@amplitude/analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { useHandler } from "@redotech/react-util/hook";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { AuthContext } from "./auth";
import { SettingsContext } from "./settings";

export interface Card {
  id: string;
  card_company: string;
  last_four_digits: string;
  expiration_date: string;
}

export interface User {
  email: string;
  name: string;
  customerId?: string;
  guestEmail?: string;
  guestName?: string;
  address?: string;
  instantRefund?: { astraUserId: string; card: Card };
  isMerchant?: boolean;
  merchantName?: string;
}

const NAME_TOKEN_KEY = "redo.user_name_token";
const EMAIL_TOKEN_KEY = "redo.email_token";
const GUEST_NAME_TOKEN_KEY = "redo.guest_name_token";
const GUEST_EMAIL_TOKEN_KEY = "redo.guest_email_token";
const ADDRESS_TOKEN_KEY = "redo.address_token";
const CUSTOMER_ID_KEY = "redo.customer_id_token";
const IS_MERCHANT_KEY = "redo.is_merchant_token";
const MERCHANT_NAME_KEY = "redo.merchant_name_token";
export const UserContext = createContext<User | undefined>(undefined);

export const SetUserContext = createContext<
  ((user: User | undefined) => void) | undefined
>(undefined);

export function UserContextProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | undefined>(undefined);
  const setUser = useHandler((value: User | undefined) => {
    if (user) {
      amplitude.reset();
    }
    if (value) {
      if (globalThis.AMPLITUDE_API_KEY) {
        amplitude.init(globalThis.AMPLITUDE_API_KEY, {
          userId: value.guestEmail || value.email,
          autocapture: {
            pageViews: false,
            sessions: false,
            formInteractions: false,
            fileDownloads: false,
            attribution: false,
            elementInteractions: {
              cssSelectorAllowlist: ["[data-track-id]"],
              actionClickAllowlist: ["[data-track-id]"],
              viewportContentUpdated: { enabled: false },
              dataAttributePrefix: "data-track-",
            },
          },
        });
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set("storeUrl", location.hostname);
        amplitude.identify(identifyEvent);
      }

      datadogLogs.setUser({ email: value.guestEmail || value.email });
    }
    setUserState(value);
  });
  const auth = useContext(AuthContext);
  const settings = useContext(SettingsContext);
  const storeURL: string | undefined = settings?.storeUrl;

  useEffect(() => {
    if (user && storeURL) {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("storeURL", storeURL);
      amplitude.identify(identifyEvent);
      datadogLogs.setGlobalContextProperty("storeURL", storeURL);
    }
  }, [user, storeURL]);

  useLayoutEffect(() => {
    if (auth) {
      setUser({
        name: localStorage.getItem(NAME_TOKEN_KEY) ?? "",
        email: localStorage.getItem(EMAIL_TOKEN_KEY) ?? "",
        guestEmail: localStorage.getItem(GUEST_EMAIL_TOKEN_KEY) ?? undefined,
        guestName: localStorage.getItem(GUEST_NAME_TOKEN_KEY) ?? undefined,
        address: (() => {
          try {
            // ignore bad data
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            JSON.parse(localStorage.getItem(ADDRESS_TOKEN_KEY)!);
            return localStorage.getItem(ADDRESS_TOKEN_KEY) ?? undefined;
          } catch {
            return;
          }
        })(),
        customerId: localStorage.getItem(CUSTOMER_ID_KEY) ?? undefined,
        isMerchant: localStorage.getItem(IS_MERCHANT_KEY) === "true",
        merchantName: localStorage.getItem(MERCHANT_NAME_KEY) ?? undefined,
      });
    } else {
      setUser(undefined);
    }
  }, [auth, setUser]);

  const setUser_ = useHandler((user: User | undefined) => {
    setUser(user);
    if (user) {
      localStorage.setItem(NAME_TOKEN_KEY, user.name);
      localStorage.setItem(EMAIL_TOKEN_KEY, user.email);
      if (user.guestEmail) {
        localStorage.setItem(GUEST_EMAIL_TOKEN_KEY, user.guestEmail);
      } else {
        localStorage.removeItem(GUEST_EMAIL_TOKEN_KEY);
      }
      if (user.guestName) {
        localStorage.setItem(GUEST_NAME_TOKEN_KEY, user.guestName);
      } else {
        localStorage.removeItem(GUEST_NAME_TOKEN_KEY);
      }
      if (user.address) {
        localStorage.setItem(ADDRESS_TOKEN_KEY, user.address);
      } else {
        localStorage.removeItem(ADDRESS_TOKEN_KEY);
      }
      if (user.customerId) {
        localStorage.setItem(CUSTOMER_ID_KEY, user.customerId);
      } else {
        localStorage.removeItem(CUSTOMER_ID_KEY);
      }
      if (user.isMerchant) {
        localStorage.setItem(IS_MERCHANT_KEY, "true");
      } else {
        localStorage.removeItem(IS_MERCHANT_KEY);
      }
      if (user.merchantName) {
        localStorage.setItem(MERCHANT_NAME_KEY, user.merchantName);
      } else {
        localStorage.removeItem(MERCHANT_NAME_KEY);
      }
    } else {
      localStorage.removeItem(NAME_TOKEN_KEY);
      localStorage.removeItem(EMAIL_TOKEN_KEY);
      localStorage.removeItem(GUEST_EMAIL_TOKEN_KEY);
      localStorage.removeItem(GUEST_NAME_TOKEN_KEY);
      localStorage.removeItem(ADDRESS_TOKEN_KEY);
      localStorage.removeItem(CUSTOMER_ID_KEY);
      localStorage.removeItem(IS_MERCHANT_KEY);
      localStorage.removeItem(MERCHANT_NAME_KEY);
    }
  });

  return (
    <UserContext.Provider value={user}>
      <SetUserContext.Provider value={setUser_}>
        {children}
      </SetUserContext.Provider>
    </UserContext.Provider>
  );
}
